// src/lib/mixpanelService.ts
import { mixpanel, isMixpanelEnabled } from './mixpanel';
import { posthogService } from './posthogService';
import type { UserProfile } from '../types/api';

/**
 * Event name constants for type safety and consistency
 */
export const MixpanelEvents = {
  // Auth events
  USER_LOGGED_IN: 'User Logged In',
  USER_SIGNED_UP: 'User Signed Up',
  USER_LOGGED_OUT: 'User Logged Out',

  // Navigation
  PAGE_VIEWED: 'Page Viewed',

  // Feature usage
  SEARCH_PERFORMED: 'Search Performed',
  FILTER_APPLIED: 'Filter Applied',
  ALERT_CREATED: 'Alert Created',
  ALERT_UPDATED: 'Alert Updated',
  ALERT_DELETED: 'Alert Deleted',
  DATA_EXPORTED: 'Data Exported',

  // Conversion funnel
  UPGRADE_PAGE_VIEWED: 'Upgrade Page Viewed',
  CHECKOUT_STARTED: 'Checkout Started',
  CHECKOUT_COMPLETED: 'Checkout Completed',
  PREMIUM_FEATURE_BLOCKED: 'Premium Feature Blocked',
} as const;

export type MixpanelEventName = (typeof MixpanelEvents)[keyof typeof MixpanelEvents];

/**
 * Properties that can be passed to track events
 */
interface TrackProperties {
  [key: string]: string | number | boolean | null | undefined;
}

/**
 * Mixpanel service wrapper providing typed methods for common operations
 */
class MixpanelService {
  /**
   * Check if tracking is enabled
   */
  private isEnabled(): boolean {
    return isMixpanelEnabled();
  }

  /**
   * Identify user after login
   * Sets the distinct_id and user profile properties
   */
  identify(user: UserProfile): void {
    const userId = String(user.id);

    if (this.isEnabled()) {
      // Set distinct_id for this user
      mixpanel.identify(userId);

      // Set user profile properties
      mixpanel.people.set({
        $email: user.email,
        $name: user.username || `${user.first_name} ${user.last_name}`.trim(),
        $first_name: user.first_name,
        $last_name: user.last_name,
        username: user.username,
        subscription_tier: user.subscription?.tier || 'free',
        is_staff: user.is_staff || false,
      });

      // Register subscription tier as super property (included in all events)
      mixpanel.register({
        subscription_tier: user.subscription?.tier || 'free',
        user_id: userId,
      });
    }

    posthogService.identify(user);
  }

  /**
   * Reset user identity on logout
   * Clears the distinct_id and super properties
   */
  reset(): void {
    if (this.isEnabled()) {
      mixpanel.reset();
    }

    posthogService.reset();
  }

  /**
   * Track a generic event with optional properties
   */
  track(eventName: MixpanelEventName | string, properties?: TrackProperties): void {
    const eventProperties = {
      ...properties,
      timestamp: new Date().toISOString(),
    };

    if (this.isEnabled()) {
      mixpanel.track(eventName, eventProperties);
    }

    posthogService.track(eventName, eventProperties);
  }

  /**
   * Track a page view with the page name
   */
  trackPageView(pageName: string, additionalProps?: TrackProperties): void {
    const pageProperties = {
      page_name: pageName,
      page_path: window.location.pathname,
      page_url: window.location.href,
      referrer: document.referrer || undefined,
      ...additionalProps,
    };

    if (this.isEnabled()) {
      mixpanel.track(MixpanelEvents.PAGE_VIEWED, {
        ...pageProperties,
        timestamp: new Date().toISOString(),
      });
    }

    posthogService.trackPageView(pageName, pageProperties);
  }

  /**
   * Track user login
   */
  trackLogin(user: UserProfile, method: string = 'email'): void {
    this.identify(user);
    this.track(MixpanelEvents.USER_LOGGED_IN, {
      method,
      subscription_tier: user.subscription?.tier || 'free',
    });
  }

  /**
   * Track user signup
   */
  trackSignup(method: string = 'email', hasInvitationCode: boolean = false): void {
    this.track(MixpanelEvents.USER_SIGNED_UP, {
      method,
      has_invitation_code: hasInvitationCode,
    });
  }

  /**
   * Track user logout
   */
  trackLogout(): void {
    this.track(MixpanelEvents.USER_LOGGED_OUT);
    this.reset();
  }

  /**
   * Track upgrade page view
   */
  trackUpgradePageViewed(source?: string): void {
    this.track(MixpanelEvents.UPGRADE_PAGE_VIEWED, {
      source,
    });
  }

  /**
   * Track checkout started
   */
  trackCheckoutStarted(planId: string, planName: string, price: number): void {
    this.track(MixpanelEvents.CHECKOUT_STARTED, {
      plan_id: planId,
      plan_name: planName,
      price,
    });
  }

  /**
   * Track checkout completed
   */
  trackCheckoutCompleted(planId: string, planName: string): void {
    this.track(MixpanelEvents.CHECKOUT_COMPLETED, {
      plan_id: planId,
      plan_name: planName,
    });
  }

  /**
   * Track premium feature blocked (upgrade prompt shown)
   */
  trackPremiumFeatureBlocked(feature: string): void {
    this.track(MixpanelEvents.PREMIUM_FEATURE_BLOCKED, {
      feature,
    });
  }

  /**
   * Track alert creation
   */
  trackAlertCreated(alertType: string, targetModel: string): void {
    this.track(MixpanelEvents.ALERT_CREATED, {
      alert_type: alertType,
      target_model: targetModel,
    });
  }

  /**
   * Track search performed
   */
  trackSearch(query: string, searchType: string, resultCount?: number): void {
    this.track(MixpanelEvents.SEARCH_PERFORMED, {
      query,
      search_type: searchType,
      result_count: resultCount,
    });
  }

  /**
   * Track filter applied
   */
  trackFilterApplied(filterType: string, filterValue: string): void {
    this.track(MixpanelEvents.FILTER_APPLIED, {
      filter_type: filterType,
      filter_value: filterValue,
    });
  }

  /**
   * Track data export
   */
  trackDataExported(exportType: string, recordCount?: number): void {
    this.track(MixpanelEvents.DATA_EXPORTED, {
      export_type: exportType,
      record_count: recordCount,
    });
  }
}

// Export singleton instance
export const mixpanelService = new MixpanelService();
